/**
 * ConsentBanner.jsx — Bandeau de consentement analytics 2 niveaux (SPEC-2026-07-03).
 *
 * UX (Jonathan) : barre fine en bas du site, disparaît au clic OU automatiquement
 * 4s après le 1er scroll, ne se réaffiche plus une fois un choix posé (cookie).
 *
 * RGPD : tant qu'aucun clic « Accept » → mode ANONYMISÉ (exempté CNIL), le scroll/auto
 * -dismiss vaut acquittement de la notice, PAS consentement full-fidelity. « Accept »
 * active le full-fidelity. Re-consentement après 6 mois (géré au montage).
 * Texte en anglais (§64). Barre fine 1 ligne, Accept + Reject calés à droite ; sur
 * mobile le message wrappe (styled + media). Preview validée : /previews/consent-banner.html.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { media, COLORS } from './ui/theme';
import { getConsent, setConsent } from '../services/AnalyticsClient';

const CONSENT_MAX_AGE_MS = 6 * 30 * 24 * 3600 * 1000; // ~6 mois (re-consentement CNIL)

function consentStillValid() {
  try {
    if (!getConsent()) return false;
    const at = parseInt(window.localStorage.getItem('ev_analytics_consent_at') || '0', 10);
    return at && (Date.now() - at) < CONSENT_MAX_AGE_MS;
  } catch (_) { return !!getConsent(); }
}

// Aperçu forcé pour QA : ajouter ?consent=preview à l'URL affiche le bandeau même
// si un choix a déjà été posé, et désactive l'auto-dismiss (pour l'inspecter). Sans
// le param = comportement normal (aucun impact sur les vrais visiteurs).
function isForcedPreview() {
  try { return new URLSearchParams(window.location.search).get('consent') === 'preview'; }
  catch (_) { return false; }
}

export default function ConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const autoTimer = useRef(null);
  const forced = isForcedPreview();

  useEffect(() => {
    // Affiche si aperçu forcé OU si aucun choix valide (< 6 mois) existant.
    if (forced || !consentStillValid()) setVisible(true);
    return () => { if (autoTimer.current) clearTimeout(autoTimer.current); };
  }, [forced]);

  useEffect(() => {
    if (!visible || forced) return undefined; // aperçu forcé : pas d'auto-dismiss
    // 1er scroll → auto-dismiss (acquittement) après 4s.
    const onScroll = () => {
      window.removeEventListener('scroll', onScroll);
      if (autoTimer.current) return;
      autoTimer.current = setTimeout(() => dismiss('anon'), 4000);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [visible, forced]);

  const dismiss = (level) => {
    setConsent(level); // 'anon' = acquittement (anonymisé) ; 'full' = full-fidelity
    setVisible(false);
    if (autoTimer.current) { clearTimeout(autoTimer.current); autoTimer.current = null; }
  };

  if (!visible) return null;

  return (
    <Bar role="dialog" aria-label="Privacy notice">
      <Msg>
        {t('ConsentMessage')}
        {' '}
        <a href="/privacy">{t('ConsentPrivacyPolicy')}</a>
      </Msg>
      <Actions>
        <Accept type="button" onClick={() => dismiss('full')}>{t('ConsentAccept')}</Accept>
        <Reject type="button" onClick={() => dismiss('anon')}>{t('ConsentReject')}</Reject>
      </Actions>
    </Bar>
  );
}

// Barre fine, 1 ligne : message ellipsé à gauche, Accept + Reject calés à droite
// (margin-left auto). Sur mobile le message wrappe en pleine largeur, boutons à droite.
const Bar = styled.div`
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 4000;
  display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;
  padding: 0.5rem 1.125rem;
  background: #0d0520; color: #ffffff;
  font-size: 0.8125rem; line-height: 1.3;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.22);
`;
const Msg = styled.span`
  flex: 1 1 auto; min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  a { color: #ffffff; text-decoration: underline; }
  ${media.sm`white-space: normal; flex-basis: 100%;`}
`;
const Actions = styled.span`
  flex: 0 0 auto; display: flex; align-items: center; gap: 0.5rem; margin-left: auto;
  /* Décalage à gauche pour dégager le widget de chat flottant (bottom-right) :
     le bouton Reject ne doit pas passer sous la bulle de chat. */
  margin-right: 4.75rem;
  ${media.sm`margin-right: 3rem;`}
`;
const Accept = styled.button`
  background: ${COLORS.accent}; color: #ffffff; border: none;
  padding: 0.375rem 1rem; border-radius: 0.5625rem; cursor: pointer;
  font-size: 0.8125rem; font-weight: 600;
`;
const Reject = styled.button`
  background: transparent; color: #ffffff; border: 1px solid rgba(255, 255, 255, 0.45);
  padding: 0.375rem 1rem; border-radius: 0.5625rem; cursor: pointer;
  font-size: 0.8125rem;
`;
